import { Box, Radio, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ScenarioAppliedAs } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-applied-as.enum';

type ScenarioAppliedAsOption = {
  value: ScenarioAppliedAs;
  title: string;
  description: string;
};

const SelectionContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: 10,
}));

const SelectionCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  display: 'flex',
  flexDirection: 'row',
  flex: 1,
  borderRadius: 8,
  border: `2px solid ${isSelected ? theme.palette.darkBlue.main : theme.palette.gray.light}`,
  cursor: 'pointer',
  backgroundColor: isSelected ? theme.palette.lightBlue.o25 : theme.palette.common.white,
  transition: 'border-color 0.2s, background-color 0.2s',
  '&:hover': {
    borderColor: theme.palette.darkBlue.main,
  },
}));

const CardContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  flex: 1,
  padding: theme.spacing(3),
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.darkBlue.main,
}));

const CardDescription = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.gray.dark,
  lineHeight: 1.4,
}));

const RadioContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  padding: theme.spacing(2),
}));

type ApplyAsSelectionProps = {
  value: ScenarioAppliedAs;
  onChange: (value: ScenarioAppliedAs) => void;
};

const options: ScenarioAppliedAsOption[] = [
  {
    value: ScenarioAppliedAs.PLAN,
    title: 'bluViuNutrition.applyFeed.applyAs.plan.title',
    description: 'bluViuNutrition.applyFeed.applyAs.plan.description',
  },
  {
    value: ScenarioAppliedAs.FORECAST,
    title: 'bluViuNutrition.applyFeed.applyAs.forecast.title',
    description: 'bluViuNutrition.applyFeed.applyAs.forecast.description',
  },
  {
    value: ScenarioAppliedAs.PLAN_AND_FORECAST,
    title: 'bluViuNutrition.applyFeed.applyAs.planAndForecast.title',
    description: 'bluViuNutrition.applyFeed.applyAs.planAndForecast.description',
  },
];

const ApplyAsSelection: React.FC<ApplyAsSelectionProps> = ({ value, onChange }) => {
  const handleSelect = (selectedValue: ScenarioAppliedAsOption) => {
    onChange(selectedValue.value);
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} >
      <Typography fontSize={14} fontWeight={500}>
        <FormattedMessage id="bluViuNutrition.applyFeed.applyAs.label" />
      </Typography>
      <SelectionContainer>
        {options.map((option) => (
          <SelectionCard
            key={option.value}
            isSelected={value === option.value}
            onClick={() => handleSelect(option)}
          >
            <CardContent>
              <CardTitle>
                <FormattedMessage id={option.title} />
              </CardTitle>
              <CardDescription>
                <FormattedMessage id={option.description} />
              </CardDescription>
            </CardContent>
            <RadioContainer>
              <Radio
                checked={value === option.value}
                onChange={() => handleSelect(option)}
                value={option.value}
                size="small"
              />
            </RadioContainer>
          </SelectionCard>
        ))}
      </SelectionContainer>
    </Box>
  );
};

export default ApplyAsSelection;
